import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalR } from '@s20/hooks/signalr.hooks';
import { useFormValues } from '@s20/components/forms/FormContext';
import { useJobQueryKey } from './common/useJobQueryKey.hook';
import { useResourceQueryKey } from './common/useResourceQueryKey.hook';

export const NotificationHandler = () => {
    const queryClient = useQueryClient();
    const dispatchGroupCompanies = useFormValues('dispatchGroupCompanies');

    const { queryKey: jobsQueryKey } = useJobQueryKey();
    const { queryKey: resourcesQueryKey } = useResourceQueryKey();

    const handleInvalidateJobQuery = useCallback((job, opts) => {
        let invalidate = false;
        let companyId = null;

        try {
            if (opts) {
                const optionPayload = JSON.parse(opts);

                companyId = optionPayload?.dispatchOfficeLocationId;
            }

            if (!job) {
                invalidate = true;
            } else if (companyId && dispatchGroupCompanies.includes(companyId)) {
                invalidate = true;
            }

            const jobData = JSON.parse(job);

            const validate = (item) =>
                dispatchGroupCompanies.includes(item.dispatchOfficeLocationId) // Raw job data
                || dispatchGroupCompanies.includes(item.dispatchLocationId); // Planning job view model

            if (Array.isArray(jobData)) {
                invalidate = jobData.some(validate);
            } else {
                invalidate = validate(jobData);
            }
        } catch (e) {
            console.info('Unable to decode job data', e);
        }

        if (invalidate) {
            queryClient.invalidateQueries({ queryKey: jobsQueryKey });
        }
    }, [dispatchGroupCompanies, jobsQueryKey, queryClient]);

    const handleInvalidateResourceQuery = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: resourcesQueryKey });
    }, [resourcesQueryKey, queryClient]);

    const hub = useSignalR('jobUpdateHub');

    useEffect(() => {
        if (hub) {
            hub.on('JobCreated', (...args) => {
                handleInvalidateJobQuery(...args);
                // Don't need to invalidate resource query
                // Created jobs don't have crews assigned yet
            });

            hub.on('JobUpdated', (...args) => {
                handleInvalidateJobQuery(...args);
                handleInvalidateResourceQuery();
            });

            hub.on('SendMultipleOperations', (...args) => {
                handleInvalidateJobQuery(...args);
                handleInvalidateResourceQuery();
            });

            hub.on('JobCancellationUpdated', (...args) => {
                handleInvalidateJobQuery(...args);
                handleInvalidateResourceQuery();
            });

            hub.on('JobsUpdatedBulk', (...args) => {
                handleInvalidateJobQuery(...args);
                handleInvalidateResourceQuery();
            });

            hub.on('JobDeleted', (...args) => {
                handleInvalidateJobQuery(...args);
                // Don't need to invalidate resource query
                // It's not possible to delete a job if crews are assigned
            });

            hub.on('StaffRelatedDataUpdated', () => {
                handleInvalidateResourceQuery();
            });

            hub.onreconnected(() => {
                queryClient.invalidateQueries({ queryKey: ['dispatchViewModel'] });

                handleInvalidateJobQuery();
                handleInvalidateResourceQuery();
            });

            try {
                hub.invoke('JoinGroup', 'Dispatch')
                    .then(() => {
                    })
                    .catch((e) => {
                        console.info('Unable to join dispatch hub', e);
                    });
            } catch (e) {
                console.info(e);
            }

            return () => {
                hub.off('JobCreated');
                hub.off('JobUpdated');
                hub.off('SendMultipleOperations');
                hub.off('JobCancellationUpdated');
                hub.off('JobsUpdatedBulk');
                hub.off('JobDeleted');
                hub.off('StaffRelatedDataUpdated');
            };
        }

        return undefined;
    }, [hub, queryClient, handleInvalidateJobQuery, handleInvalidateResourceQuery]);

    return null;
};